import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { AuthType } from 'constants/constants'

import { Box, Button, Grid, Modal, Typography } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { AgGridReact } from 'ag-grid-react'
import { EditStatusCellRenderer, LoadingOverlay, SearchBox } from 'components/commonparts'
import { useCenterStockItem } from 'hooks/useCenterStockItem'
import {
  collectRowNodesFromGridSelection,
  getClipboardContextMenuItems,
  useGridEdit,
} from 'hooks/useGridEdit'
import { useGridSearch } from 'hooks/useGridSearch'
import { DialogWrapper } from 'templates'
import { initRecordEditEx, type RecordEditEx } from 'types/dataGridTypes'

import type { DefaultMenuItem, GetContextMenuItemsParams, IRowNode, MenuItemDef } from 'ag-grid-community'
import type { ColDef } from 'ag-grid-enterprise'
import type {
  AlertMstCenterStockItem,
  GetMstCenterStockItemCenterStockItemGetRequest,
  MstCenter,
  MstItem,
  ResponseResult,
} from 'api-client'

export type StockCenterItemAlertDialogEdit = {
  orgCenterId: string
  orgItemCode: string
  minRecDate?: Date | null
  masterRegister?: boolean
  isDiscontinued?: boolean
  isMuted?: boolean
}

type StockCenterItemAlertDialogProps = {
  open: boolean
  onClose: () => void
  onApplyEdits: (edits: StockCenterItemAlertDialogEdit[]) => void
  onNavigateToRecord: (target: { orgCenterId: string; orgItemCode: string }) => void
  resetSignal: number
  searchParam: GetMstCenterStockItemCenterStockItemGetRequest
  center: MstCenter[]
  items: MstItem[]
}

type AlertMstCenterStockItemWithId = AlertMstCenterStockItem & RecordEditEx

/** Alert API has no orgItemCode param; filter client-side when search specifies a product. */
function filterAlertRowsByOrgItemCode(
  list: AlertMstCenterStockItem[],
  orgItemCode?: string | null
): AlertMstCenterStockItem[] {
  const code = orgItemCode?.trim()
  if (!code) return list
  return list.filter(
    (item) =>
      item.masterNotFound === true || String(item.orgItemCode ?? '').trim() === code
  )
}

function StockCenterItemAlertDialog({
  open,
  onClose,
  onApplyEdits,
  onNavigateToRecord,
  resetSignal,
  searchParam,
  center,
  items,
}: StockCenterItemAlertDialogProps) {
  const { loginUserInfo } = useAuthContext()
  const isReadOnly = loginUserInfo?.authType === AuthType.ReadOnly
  const dialogGridSearch = useGridSearch({ autoShowOnCtrlF: open })
  const { fetchAlertCenterStockItems, isAlertCenterStockItemsLoading } = useCenterStockItem()
  const [rows, setRows] = useState<AlertMstCenterStockItemWithId[]>([])
  const [pendingEdits, setPendingEdits] = useState<Record<string, StockCenterItemAlertDialogEdit>>({})
  const [hasLoaded, setHasLoaded] = useState(false)
  const [, setHasGridEdited] = useState(false)
  const [, setResultMessage] = useState<ResponseResult | undefined>(undefined)

  const {
    onGridReady,
    dateFormatter,
    numberValueFormatter,
    localeText,
    defaultColDef,
    createCellClassRules,
    selectValueFormatter,
    numberValueParserZero,
  } = useGridEdit({
    gridRef: dialogGridSearch.gridRef as React.RefObject<AgGridReact>,
    isLoading: isAlertCenterStockItemsLoading,
    isUpdating: false,
    onUploadData: undefined,
    updateTrigger: undefined,
    setHasGridEdited,
    setResultMessage,
    columnMapping: {},
    primayKey: ['orgCenterId', 'orgItemCode'],
    createBlankRow: () => ({ ...initRecordEditEx(true) }),
    fieldValidate: () => false,
  })

  useEffect(() => {
    setRows([])
    setPendingEdits({})
    setHasLoaded(false)
  }, [resetSignal])

  const applyRowsForAction = useCallback(
    (
      selectedNodes: IRowNode[],
      actionLabel: string,
      updater: (row: AlertMstCenterStockItemWithId) => AlertMstCenterStockItemWithId,
      editBuilder: (row: AlertMstCenterStockItemWithId) => StockCenterItemAlertDialogEdit
    ) => {
      if (selectedNodes.length === 0) return
      const selectedRowIds = new Set(selectedNodes.map((node) => node.data?.id))

      setRows((prev) =>
        prev.map((row) => {
          if (!selectedRowIds.has(row.id)) return row
          const nextEditedFields = new Set(row.editedFields)
          nextEditedFields.add(`action:${actionLabel}`)
          return {
            ...updater(row),
            isEdited: true,
            editedFields: nextEditedFields,
          }
        })
      )

      setPendingEdits((prev) => {
        const next = { ...prev }
        selectedNodes.forEach((node) => {
          const row = node.data as AlertMstCenterStockItemWithId
          const centerKey = row.orgCenterId ?? ''
          const itemKey = row.orgItemCode ?? ''
          const mapKey = `${centerKey}|${itemKey}`
          if (!centerKey || !itemKey) return
          next[mapKey] = {
            ...(next[mapKey] ?? { orgCenterId: centerKey, orgItemCode: itemKey }),
            ...editBuilder(row),
          }
        })
        return next
      })
    },
    []
  )

  useEffect(() => {
    const load = async () => {
      if (!open || hasLoaded) return

      const response = await fetchAlertCenterStockItems({
        enabled: false,
        companyId: searchParam.companyId,
        centerId: searchParam.centerId || undefined,
      })
      if (!response) return

      const filteredList = filterAlertRowsByOrgItemCode(
        response.data.list,
        searchParam.orgItemCode
      )

      setRows(
        filteredList.map((item) => ({
          ...item,
          ...initRecordEditEx(),
        }))
      )
      setHasLoaded(true)
      // API で行を差し替えたときだけフィルター・検索をリセット（再オープンのみでは残す）
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const gridApi = dialogGridSearch.gridRef.current?.api
          if (gridApi && !gridApi.isDestroyed()) {
            gridApi.setFilterModel(null)
          }
          dialogGridSearch.clearSearch()
        })
      })
    }

    load()
  }, [open, hasLoaded, searchParam.companyId, searchParam.centerId, searchParam.orgItemCode])

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (MenuItemDef | DefaultMenuItem)[] => {
      const targets = collectRowNodesFromGridSelection(params.api, params.node)
      if (targets.length === 0) return []

      const canMasterRegister = targets.every((node) => node.data?.masterNotFound === true)
      const canShow =
        targets.length === 1 &&
        targets[0].data?.masterNotFound === false &&
        !!targets[0].data?.orgCenterId &&
        !!targets[0].data?.orgItemCode

      return [
        ...getClipboardContextMenuItems(params),
        'separator',
        {
          name: 'マスタ追加',
          disabled: !canMasterRegister || isReadOnly,
          action: () => {
            if (isReadOnly) return
            const nodes = collectRowNodesFromGridSelection(params.api, params.node)
            applyRowsForAction(nodes, 'マスタ追加', (row) => row, (row) => ({
              orgCenterId: row.orgCenterId ?? '',
              orgItemCode: row.orgItemCode ?? '',
              minRecDate: row.minRecDate ?? null,
              masterRegister: true,
            }))
          },
        },
        {
          name: '取扱終了',
          disabled: isReadOnly,
          action: () => {
            if (isReadOnly) return
            const nodes = collectRowNodesFromGridSelection(params.api, params.node)
            applyRowsForAction(
              nodes,
              '取扱終了',
              (row) => ({ ...row, isDiscontinued: true }),
              (row) => ({ orgCenterId: row.orgCenterId ?? '', orgItemCode: row.orgItemCode ?? '', isDiscontinued: true })
            )
          },
        },
        {
          name: '通知不要',
          disabled: isReadOnly,
          action: () => {
            if (isReadOnly) return
            const nodes = collectRowNodesFromGridSelection(params.api, params.node)
            applyRowsForAction(
              nodes,
              '通知不要',
              (row) => ({ ...row, isMuted: true }),
              (row) => ({ orgCenterId: row.orgCenterId ?? '', orgItemCode: row.orgItemCode ?? '', isMuted: true })
            )
          },
        },
        'separator',
        {
          name: '表示',
          disabled: !canShow,
          action: () => {
            const nodes = collectRowNodesFromGridSelection(params.api, params.node)
            if (nodes.length !== 1) return
            const row = nodes[0].data as AlertMstCenterStockItemWithId
            if (!row || row.masterNotFound || !row.orgCenterId || !row.orgItemCode) return
            onNavigateToRecord({ orgCenterId: row.orgCenterId, orgItemCode: row.orgItemCode })
            onClose()
          },
        },
      ]
    },
    [applyRowsForAction, isReadOnly, onClose, onNavigateToRecord]
  )

  const handleOk = useCallback(() => {
    if (isReadOnly) {
      onClose()
      return
    }
    onApplyEdits(Object.values(pendingEdits))
    onClose()
  }, [isReadOnly, onApplyEdits, onClose, pendingEdits])

  const columns = useMemo<ColDef[]>(
    () => [
      {
        headerName: '!',
        field: 'editInfo',
        width: 70,
        pinned: 'left',
        cellRenderer: EditStatusCellRenderer,
        valueGetter: (params: any) => {
          const edited = Array.from((params.data?.editedFields ?? new Set<string>()) as Set<string>)
            .filter((v) => v.startsWith('action:'))
            .map((v) => v.replace('action:', ''))
          return edited.join(',\n')
        },
        tooltipValueGetter: (params: any) => {
          const edited = Array.from((params.data?.editedFields ?? new Set<string>()) as Set<string>)
            .filter((v) => v.startsWith('action:'))
            .map((v) => v.replace('action:', ''))
          return edited.join(',\n')
        },
      },
      { headerName: 'アラート種別', field: 'alertTypeName', width: 200 },
      {
        headerName: '自社センター',
        field: 'orgCenterId',
        width: 200,
        valueFormatter: (params: any) => selectValueFormatter(params.value, center, 'orgCenterId', 'centerName'),
      },
      {
        headerName: '社内商品コード',
        field: 'orgItemCode',
        width: 200,
        valueFormatter: (params: any) => {
          const formatted = selectValueFormatter(params.value, items, 'orgItemCode', 'itemName')
          if (formatted) return formatted
          const code = params.value ?? ''
          const name = params.data?.mstItemItemName ?? ''
          return code && name ? `[${code}]${name}` : code
        },
      },
      // { headerName: '商品名', field: 'mstItemItemName', width: 200 },
      {
        headerName: 'マスタ未登録',
        children: [
          {
            headerName: 'マスタ未登録',
            field: 'masterNotFound',
            cellClassRules: createCellClassRules('masterNotFound', true, 'alert_middle'),
            cellRenderer: 'agCheckboxCellRenderer',
            cellClass: 'boolean-cell',
            width: 150
          },
          {
            headerName: '最小実績日',
            field: 'minRecDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
            width: 150,
          },
        ]
      },
      {
        headerName: '休止アラート',
        children: [
          {
            headerName: '休止アラート',
            field: 'isInactive',
            cellClassRules: createCellClassRules('isInactive', true, 'alert_middle'),
            cellRenderer: 'agCheckboxCellRenderer',
            cellClass: 'boolean-cell',
            width: 150
          },
          { headerName: '最終出荷日',
            field: 'lastShipDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
            width: 150,
          },
          { headerName: '最新在庫数',
            field: 'lastStockQuantity',
            cellClass: 'number-cell',
            filter: 'agNumberColumnFilter',
            valueParser: numberValueParserZero,
            valueFormatter: numberValueFormatter,
            width: 150,
          },
        ]
      },
      // {
      //   headerName: 'リニューアル設定',
      //   children: [
      //     { headerName: '適用開始日確認',
      //       field: 'unmatchRenewal',
      //       cellClassRules: createCellClassRules('unmatchRenewal', true, 'alert_middle'),
      //       cellRenderer: 'agCheckboxCellRenderer',
      //       cellClass: 'boolean-cell',
      //       width: 200 },
      //     { headerName: '後継社内商品コード', field: 'replaceOrgItemCode', width: 200 },
      //     { headerName: 'JANコード（ケース）', field: 'janCase', width: 200 },
      //     { headerName: '適用開始日', field: 'trStartDate', valueFormatter: dateFormatter, width: 200 },
      //     { headerName: '最新商品フラグ', field: 'recencyFlg', cellRenderer: 'agCheckboxCellRenderer',
      //       cellClass: 'boolean-cell',
      //       width: 200 },
      //     { headerName: '前の社内商品コード', field: 'prevOrgItemCode', width: 200 },
      //     { headerName: '次の社内商品コード', field: 'nextOrgItemCode', width: 200 },
      //   ]
      // },
      {
        headerName: '発注設定',
        children: [
          {
            headerName: '発注ロット確認',
            field: 'unmatchLot',
            cellClassRules: createCellClassRules('unmatchLot', true, 'alert_middle'),
            cellRenderer: 'agCheckboxCellRenderer',
            cellClass: 'boolean-cell',
            width: 150 
          },
          {
            headerName: '発注ロット数', field: 'trCaseLot',
            cellClass: 'number-cell',
            filter: 'agNumberColumnFilter',
            valueParser: numberValueParserZero,
            valueFormatter: numberValueFormatter,
            width: 150,
          },
          {
            headerName: '在庫日数確認',
            field: 'unmatchStockdays',
            cellClassRules: createCellClassRules('unmatchStockdays', true, 'alert_middle'),
            cellRenderer: 'agCheckboxCellRenderer',
            cellClass: 'boolean-cell',
            width: 150
          },
          {
            headerName: '下限在庫日数',
            field: 'trStockDaysSetting',
            cellClass: 'number-cell',
            filter: 'agNumberColumnFilter',
            valueParser: numberValueParserZero,
            valueFormatter: numberValueFormatter,
            width: 150,
          },
          {
            headerName: '直近30日出荷数合計', field: 'lastShipSummary',
            cellClass: 'number-cell',
            filter: 'agNumberColumnFilter',
            valueParser: numberValueParserZero,
            valueFormatter: numberValueFormatter,
            width: 150,
          },
          {
            headerName: '直近30日出荷回数', field: 'lastShipCount',
            cellClass: 'number-cell',
            filter: 'agNumberColumnFilter',
            valueParser: numberValueParserZero,
            valueFormatter: numberValueFormatter,
            width: 160,
          },
        ]
      },
      {
        headerName: '新商品アラート',
        children: [
          {
            headerName: '新商品アラート',
            field: 'isMinShipDateAlert',
            cellClassRules: createCellClassRules('isMinShipDateAlert', true, 'alert_middle'),
            cellRenderer: 'agCheckboxCellRenderer',
            cellClass: 'boolean-cell',
            width: 150 },
          {
            headerName: '最小出荷実績日',
            field: 'minShipDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
            width: 150,
          },
        ]
      },
      {
        headerName: '終売アラート',
        children: [
          {
            headerName: '終売アラート',
            field: 'isEndSaleAlert',
            cellClassRules: createCellClassRules('isEndSaleAlert', true, 'alert_middle'),
            cellRenderer: 'agCheckboxCellRenderer',
            cellClass: 'boolean-cell',
            width: 150 },
          { 
            headerName: '終売日',
            field: 'mstItemSalesEndDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
            width: 150,
          },
          { 
            headerName: '終売アラート開始日',
            field: 'salesEndAlertDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
            width: 150,
          },
        ]
      },


    ],
    [center, createCellClassRules, dateFormatter, items, selectValueFormatter]
  )

  return (
    <Modal keepMounted open={open} onClose={onClose}>
      <DialogWrapper>
        <Box sx={{ pt: 2 }}>
          <Typography component="div" variant="largeBold">
            センター在庫管理アラート
          </Typography>

          <SearchBox
            gridRef={dialogGridSearch.searchBoxProps.gridRef}
            findSearchValue={dialogGridSearch.searchBoxProps.findSearchValue}
            setFindSearchValue={dialogGridSearch.searchBoxProps.setFindSearchValue}
            activeMatchNum={dialogGridSearch.searchBoxProps.activeMatchNum}
            onFindChanged={dialogGridSearch.searchBoxProps.onFindChanged}
            isVisible={dialogGridSearch.searchBoxProps.isVisible}
            onHide={dialogGridSearch.searchBoxProps.onHide}
          />

          <div style={{ height: '70vh', width: '100%' }} className="ag-theme-balham">
            <AgGridReact
              ref={dialogGridSearch.gridRef}
              rowData={rows}
              getRowId={(params) => params.data.id || ''}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              localeText={localeText}
              onGridReady={onGridReady}
              loadingOverlayComponent={LoadingOverlay}
              loading={isAlertCenterStockItemsLoading}
              findSearchValue={dialogGridSearch.gridSearchProps.findSearchValue}
              onFindChanged={dialogGridSearch.gridSearchProps.onFindChanged}
              getContextMenuItems={getContextMenuItems}
              cellSelection={{
                enableHeaderHighlight: true,
                handle: { mode: 'fill', direction: 'xy' },
              }}
              suppressRowClickSelection={false}
            />
          </div>
          <Grid direction="row" container justifyContent="end" sx={{ mt: 2 }}>
            <Grid component="div">
              <Button variant="contained" color="secondary" onClick={handleOk}>
                OK
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogWrapper>
    </Modal>
  )
}

export default StockCenterItemAlertDialog
